/**
 * "Kopiera från befintligt avtal" — MVP stand-in for pre-filled
 * templates (per user ask 2026-05-15). Lets a user drafting a fresh
 * contract clone parties (orgs only), property, attachments, and field
 * values from any existing same-template contract they can already see.
 *
 * Field values are organised by group + per-field copyability (per
 * ADR-0050): one checkbox per template input-group, defaulting checked
 * for structural groups and unchecked for per-contract ones. Each group
 * carries its `'auto'` fields (copied when the group is checked) and any
 * `'manual'` fields (rent amount, absolute dates) the user can opt into
 * under "Anpassa". The client resolves the selection into explicit field
 * keys + attachment ids; the server re-checks copyability.
 *
 * PII never leaks through this surface: the server filters out
 * `privacy_class !== 'searchable_org'` parties regardless of what the
 * client requests, and `autofill` / `derivedFromParent` fields resolve to
 * copy class `'never'` so individual-personnummer / address autofills
 * from PII parties can never tag along through the `values` map either.
 *
 * Two steps:
 *   1. Search same-templateKey contracts (live, debounced).
 *   2. Toggle the groups / pieces to copy via checkboxes; submit.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  customSectionAnchorRef,
  effectiveCopyClass,
  getTemplateVersion,
  isFieldVisible,
  optionCode,
  readCustomSections,
  type FieldOption,
} from '@hyranai/regta-templates';
import {
  tenantApi,
  type ContractDetail,
  type ContractListRow,
  type ContractParty,
} from '../api.js';
import { useTenantSession } from '../auth-context.js';
import { useInvalidate, useTenantQuery } from '../query.js';
import { useSigningDraft } from './SigningPlanPanel.js';

type SourceRow = {
  id: string;
  title: string;
  status: ContractListRow['status'];
  groupId: string;
  createdBy: string | null;
  updatedAt: string;
  templateKey: string;
  templateVersion: string;
};

const ROLE_LABEL: Record<ContractParty['role'], string> = {
  lessor: 'Hyresvärd',
  lessee: 'Hyresgäst',
  guarantor: 'Borgensman',
  witness: 'Vittne',
  other: 'Annan',
};

const STATUS_LABEL: Record<ContractListRow['status'], string> = {
  draft: 'Utkast',
  pending: 'Klar för granskning',
  in_review: 'Under granskning',
  awaiting_manual_signature: 'Väntar på underskrift',
  sent: 'Skickat',
  viewed: 'Öppnat',
  awaiting_signature: 'Väntar på signering',
  signed: 'Signerat',
  archived: 'Arkiverat',
  cancelled: 'Avbrutet',
};

type CopyField = { key: string; label: string };
type CopyGroup = {
  id: string;
  label: string;
  defaultChecked: boolean;
  /** Copied whenever the group is checked. */
  auto: CopyField[];
  /** Per-contract fields (rent, dates) — opt-in even when the group is checked. */
  manual: CopyField[];
};

const isEmptyValue = (v: unknown): boolean =>
  v === undefined ||
  v === null ||
  v === '' ||
  (Array.isArray(v) && v.length === 0);

/**
 * A beta (IR) contract stores option codes (ADR-0062 / ADR-0099); the
 * legacy schema's visibility rules compare option values. Map codes back
 * so a beta source offers the same fields as a legacy one.
 */
function legacyChoiceView(
  schema: ReadonlyArray<{ key: string; options?: ReadonlyArray<FieldOption> }>,
  values: Record<string, unknown>,
): Record<string, unknown> {
  const out = { ...values };
  for (const f of schema) {
    if (!f.options?.length) continue;
    const toValue = (raw: unknown) =>
      typeof raw === 'string'
        ? (f.options!.find((o) => optionCode(o) === raw)?.value ?? raw)
        : raw;
    const v = values[f.key];
    out[f.key] = Array.isArray(v) ? v.map(toValue) : toValue(v);
  }
  return out;
}

/**
 * Build the copy groups for a source contract from its template metadata
 * (per ADR-0050). Groups follow `metadata.inputSections`; fields whose
 * section isn't in any input-section fall into a synthetic "Övrigt"
 * group. Templates without `inputSections` group by raw section. Only
 * copyable (`effectiveCopyClass !== 'never'`), currently-visible fields
 * that actually hold a value on the source are offered.
 */
function buildCopyGroups(
  templateKey: string,
  templateVersion: string,
  rawValues: Record<string, unknown>,
): CopyGroup[] {
  const tmpl = getTemplateVersion(templateKey, templateVersion);
  if (!tmpl) return [];
  const values = legacyChoiceView(tmpl.variableSchema, rawValues);
  const inputSections = tmpl.metadata.inputSections ?? [];
  const sectionTitle = new Map<string, string>(
    (tmpl.metadata.sections ?? []).map((s) => [s.ref, s.title]),
  );
  const refToGroup = new Map<
    string,
    { id: string; label: string; defaultChecked: boolean }
  >();
  for (const is of inputSections) {
    for (const ref of is.sectionRefs) {
      refToGroup.set(ref, {
        id: is.id,
        label: is.label,
        defaultChecked: is.copyDefault ?? true,
      });
    }
  }

  const resolveGroup = (sectionRef: string) => {
    if (inputSections.length > 0) {
      return (
        refToGroup.get(sectionRef) ?? {
          id: '__ovrigt',
          label: 'Övrigt',
          defaultChecked: true,
        }
      );
    }
    return {
      id: sectionRef,
      label: sectionTitle.get(sectionRef) ?? sectionRef,
      defaultChecked: true,
    };
  };

  const byId = new Map<string, CopyGroup>();
  const order: string[] = [];
  const ensure = (g: { id: string; label: string; defaultChecked: boolean }) => {
    let group = byId.get(g.id);
    if (!group) {
      group = { ...g, auto: [], manual: [] };
      byId.set(g.id, group);
      order.push(g.id);
    }
    return group;
  };

  for (const f of tmpl.variableSchema) {
    const cc = effectiveCopyClass(f);
    if (cc === 'never') continue;
    if (!isFieldVisible(f, values)) continue;
    if (isEmptyValue(values[f.key])) continue;
    // Sub-clause section paths are dotted ('skatter.moms.tenant'); the
    // input-group binds to the top-level section ref.
    const sectionRef = f.section.split('.')[0]!;
    const group = ensure(resolveGroup(sectionRef));
    const entry: CopyField = { key: f.key, label: f.label };
    if (cc === 'manual') group.manual.push(entry);
    else group.auto.push(entry);
  }

  const fieldGroups = order
    .map((id) => byId.get(id)!)
    .filter((g) => g.auto.length > 0 || g.manual.length > 0);

  // Per-contract custom sections (ADR-0060) copy as one group each, keyed
  // `custom:<id>`. The single `auto` field's key carries the same token;
  // the backend recognises the `custom:` prefix and copies the section
  // object (with a fresh id) into the target's __custom_sections.
  const customGroups: CopyGroup[] = readCustomSections(values, 'final').map(
    (cs) => {
      const key = customSectionAnchorRef(cs.id);
      const label = cs.title.trim() || 'Egen sektion';
      return {
        id: key,
        label,
        defaultChecked: true,
        auto: [{ key, label }],
        manual: [],
      };
    },
  );

  return [...fieldGroups, ...customGroups];
}

export function CopyFromContractModal({
  contractId,
  onClose,
  onCopied,
}: {
  contractId: string;
  onClose: () => void;
  onCopied: () => Promise<void> | void;
}) {
  const { token } = useTenantSession();
  const invalidate = useInvalidate();

  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);

  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null);
  const [copying, setCopying] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const sourcesQ = useTenantQuery(
    'contracts.list_copy_sources',
    (cid, q) =>
      tenantApi.contracts.list_copy_sources({
        contract_id: cid as string,
        q: (q as string) || undefined,
      }),
    [contractId, debounced],
    { ignoreActiveGroup: true, revalidateOnFocus: false },
  );
  const sources: SourceRow[] = sourcesQ.data?.contracts ?? [];

  const sourceDetailQ = useTenantQuery(
    'contracts.get',
    (sid) => tenantApi.contracts.get(sid as string),
    selectedSourceId ? [selectedSourceId] : [],
    { ignoreActiveGroup: true, enabled: !!selectedSourceId, revalidateOnFocus: false },
  );
  const sourcePartiesQ = useTenantQuery(
    'contract_parties.list',
    (sid) =>
      tenantApi.contract_parties.list({ contract_id: sid as string }),
    selectedSourceId ? [selectedSourceId] : [],
    { ignoreActiveGroup: true, enabled: !!selectedSourceId, revalidateOnFocus: false },
  );

  const sourceDetail: ContractDetail | null =
    sourceDetailQ.data?.contract ?? null;
  const sourceParties: ContractParty[] = sourcePartiesQ.data?.parties ?? [];

  // Only orgs eligible to copy — PII parties never appear here, server enforces too.
  const orgParties = useMemo(
    () => sourceParties.filter((p) => p.privacyClass === 'searchable_org'),
    [sourceParties],
  );
  const piiPartyCount = sourceParties.length - orgParties.length;

  const [partyIds, setPartyIds] = useState<Set<string>>(new Set());
  // Property defaults off — most copy flows are "use the same hyresvärd
  // for a different lägenhet", so pre-checking the property leads to the
  // wrong outcome more often than the right one. Lessees default off for
  // the same reason: the lessee almost always changes between contracts.
  // Only the lessor + structural field groups are pre-checked.
  const [copyProperty, setCopyProperty] = useState(false);
  const [copyLinkedProperties, setCopyLinkedProperties] = useState(false);
  // Per-attachment selection (template-kind default on, upload-kind off).
  const [attachmentIds, setAttachmentIds] = useState<Set<string>>(new Set());
  // Signing group (ADR-0100): the source's signing plan — planned signers
  // (names + capacity) and signing options. Offered only when the source
  // has one; checked by default.
  const sourcePlan = useSigningDraft(selectedSourceId).data;
  const sourceHasPlan =
    !!sourcePlan && (!sourcePlan.signers_auto || Object.keys(sourcePlan.options).length > 0);
  const [copySigning, setCopySigning] = useState(true);
  // Field-value groups (per ADR-0050): which group checkboxes are on,
  // which `manual` fields the user opted into, and which groups are
  // expanded to show their per-contract fields.
  const [groupChecked, setGroupChecked] = useState<Record<string, boolean>>({});
  const [manualChecked, setManualChecked] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Default each eligible party to "checked" only when role === 'lessor'.
  // Lessees / guarantors / witnesses start unchecked since they typically
  // differ between contracts even when the rest of the deal is reused.
  useEffect(() => {
    if (!sourceDetail || !orgParties.length) {
      setPartyIds(new Set());
      return;
    }
    setPartyIds(
      new Set(orgParties.filter((p) => p.role === 'lessor').map((p) => p.id)),
    );
  }, [sourceDetail?.id, orgParties.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePartyId = (id: string) => {
    setPartyIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const valueGroups = useMemo<CopyGroup[]>(() => {
    if (!sourceDetail) return [];
    return buildCopyGroups(
      sourceDetail.templateKey,
      sourceDetail.templateVersion,
      sourceDetail.values ?? {},
    );
  }, [sourceDetail?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const attachments = sourceDetail?.attachments ?? [];

  // Seed defaults whenever a new source is selected: group checkboxes
  // follow each group's `copyDefault`; `manual` fields start off;
  // template-kind bilagor default on, upload-kind (per-contract) off.
  useEffect(() => {
    const gc: Record<string, boolean> = {};
    for (const g of valueGroups) gc[g.id] = g.defaultChecked;
    setGroupChecked(gc);
    setManualChecked(new Set());
    setExpandedGroups(new Set());
    setAttachmentIds(
      new Set(
        attachments.filter((a) => a.kind === 'template').map((a) => a.id),
      ),
    );
  }, [sourceDetail?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleGroup = (id: string) =>
    setGroupChecked((prev) => ({ ...prev, [id]: !prev[id] }));
  const toggleExpand = (id: string) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleManual = (key: string) =>
    setManualChecked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const toggleAttachment = (id: string) =>
    setAttachmentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const hasProperty = !!sourceDetail?.propertyId;
  const linkedCount = sourceDetail?.linkedProperties?.length ?? 0;
  const isMultiProperty = linkedCount > 1;
  const attachmentsCount = attachments.length;

  const submit = async () => {
    if (!token || !selectedSourceId) return;
    // Resolve the group/field selection into explicit field keys: every
    // `auto` field of a checked group, plus opted-in `manual` fields of
    // checked groups (a manual tick is ignored when its group is off).
    const valueFieldKeys: string[] = [];
    for (const g of valueGroups) {
      if (!groupChecked[g.id]) continue;
      for (const f of g.auto) valueFieldKeys.push(f.key);
      for (const f of g.manual) {
        if (manualChecked.has(f.key)) valueFieldKeys.push(f.key);
      }
    }
    setCopying(true);
    setErr(null);
    try {
      await tenantApi.contracts.copy_from({
        target_contract_id: contractId,
        source_contract_id: selectedSourceId,
        options: {
          party_ids: Array.from(partyIds),
          copy_property: copyProperty && hasProperty && !isMultiProperty,
          copy_linked_properties: copyLinkedProperties && isMultiProperty,
          attachment_ids: Array.from(attachmentIds),
          value_field_keys: valueFieldKeys,
          copy_signing: copySigning && sourceHasPlan,
        },
      });
      await Promise.all([
        invalidate('contracts.get', contractId),
        invalidate('contract_parties.list', contractId),
        invalidate('contract_attachments.list', contractId),
        invalidate('contract_properties.list', contractId),
        invalidate('signing.get_draft', contractId),
      ]);
      await onCopied();
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setCopying(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-6"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-2xl flex-col gap-3 rounded bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">
            Kopiera från befintligt avtal
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-stone-500 hover:text-stone-800"
          >
            Stäng
          </button>
        </div>

        <p className="text-xs leading-relaxed text-stone-500">
          Välj ett tidigare avtal av samma typ att förifylla från. Personuppgifter
          kopieras inte — endast organisationer som motpart, fastighet, bilagor och
          fält som datum, hyra och klausuler kan kopieras.
        </p>

        {err && (
          <div className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700">
            {err}
          </div>
        )}

        {!selectedSourceId ? (
          <>
            <input
              type="search"
              placeholder="Sök avtal (titel, fastighet, motpart…)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full rounded border border-stone-300 px-3 py-2 text-sm"
              autoFocus
            />
            <div className="flex-1 overflow-y-auto rounded border border-stone-200">
              {sourcesQ.isLoading ? (
                <div className="p-3 text-sm text-stone-400">Laddar…</div>
              ) : sources.length === 0 ? (
                <div className="p-3 text-sm text-stone-500">
                  Inga avtal hittades.
                </div>
              ) : (
                <ul className="divide-y">
                  {sources.map((s) => (
                    <li key={s.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedSourceId(s.id)}
                        className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-stone-50"
                      >
                        <div className="text-sm font-medium text-stone-800">
                          {s.title}
                        </div>
                        <div className="text-xs text-stone-500">
                          {STATUS_LABEL[s.status] ?? s.status} ·{' '}
                          {new Date(s.updatedAt).toLocaleDateString('sv-SE')}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between rounded border border-stone-200 bg-stone-50 px-3 py-2">
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">
                  {sourceDetail?.title ?? 'Laddar…'}
                </div>
                <div className="text-xs text-stone-500">
                  Källa för kopiering
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedSourceId(null)}
                className="text-xs text-stone-500 hover:underline"
              >
                Byt
              </button>
            </div>

            {sourceDetailQ.isLoading || sourcePartiesQ.isLoading ? (
              <div className="text-sm text-stone-400">Läser källavtal…</div>
            ) : (
              <div className="flex-1 overflow-y-auto">
                <fieldset className="mb-3">
                  <legend className="mb-1 text-xs font-medium uppercase tracking-wider text-stone-500">
                    Motparter
                  </legend>
                  {orgParties.length === 0 ? (
                    <div className="text-xs text-stone-500">
                      Inga kopieringsbara motparter på källan.
                      {piiPartyCount > 0 && (
                        <>
                          {' '}
                          ({piiPartyCount} part
                          {piiPartyCount === 1 ? '' : 'er'} med personuppgifter
                          hoppas över.)
                        </>
                      )}
                    </div>
                  ) : (
                    <ul className="flex flex-col gap-1">
                      {orgParties.map((p) => (
                        <li key={p.id}>
                          <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-stone-50">
                            <input
                              type="checkbox"
                              checked={partyIds.has(p.id)}
                              onChange={() => togglePartyId(p.id)}
                            />
                            <span className="text-sm">
                              <span className="font-medium">{p.legalName}</span>
                              <span className="text-stone-500">
                                {' '}
                                · {ROLE_LABEL[p.role]}
                                {p.idNumber ? ` · ${p.idNumber}` : ''}
                              </span>
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                  {piiPartyCount > 0 && orgParties.length > 0 && (
                    <p className="mt-1 px-2 text-[11px] text-stone-500">
                      {piiPartyCount} part{piiPartyCount === 1 ? '' : 'er'} med
                      personuppgifter visas inte och kan inte kopieras.
                    </p>
                  )}
                </fieldset>

                {(hasProperty || isMultiProperty) && (
                  <fieldset className="mb-3">
                    <legend className="mb-1 text-xs font-medium uppercase tracking-wider text-stone-500">
                      Fastighet
                    </legend>
                    {isMultiProperty ? (
                      <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-stone-50">
                        <input
                          type="checkbox"
                          checked={copyLinkedProperties}
                          onChange={(e) =>
                            setCopyLinkedProperties(e.target.checked)
                          }
                        />
                        <span className="text-sm">
                          Kopiera fastigheter ({linkedCount} st)
                        </span>
                      </label>
                    ) : (
                      <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-stone-50">
                        <input
                          type="checkbox"
                          checked={copyProperty}
                          onChange={(e) => setCopyProperty(e.target.checked)}
                        />
                        <span className="text-sm">
                          Kopiera fastighet
                          {sourceDetail?.linkedProperties?.[0]?.name
                            ? ` (${sourceDetail.linkedProperties[0].name})`
                            : ''}
                        </span>
                      </label>
                    )}
                  </fieldset>
                )}

                {attachmentsCount > 0 && (
                  <fieldset className="mb-3">
                    <legend className="mb-1 text-xs font-medium uppercase tracking-wider text-stone-500">
                      Bilagor
                    </legend>
                    <ul className="flex flex-col gap-1">
                      {attachments.map((a) => (
                        <li key={a.id}>
                          <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-stone-50">
                            <input
                              type="checkbox"
                              checked={attachmentIds.has(a.id)}
                              onChange={() => toggleAttachment(a.id)}
                            />
                            <span className="text-sm">
                              <span className="font-medium">
                                {a.displayName}
                              </span>
                              <span className="text-stone-500">
                                {' · '}
                                {a.kind === 'upload'
                                  ? 'Uppladdad'
                                  : 'Från mall'}
                                {a.number ? ` · Bilaga ${a.number}` : ''}
                              </span>
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-1 px-2 text-[11px] text-stone-500">
                      Mallbilagor är förkryssade. Uppladdade bilagor (t.ex.
                      planlösning) är ofta avtalsspecifika och kopieras bara om
                      du väljer dem.
                    </p>
                  </fieldset>
                )}

                {sourceHasPlan && (
                  <fieldset className="mb-3">
                    <legend className="mb-1 text-xs font-medium uppercase tracking-wider text-stone-500">
                      Signering
                    </legend>
                    <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-stone-50">
                      <input
                        type="checkbox"
                        checked={copySigning}
                        onChange={(e) => setCopySigning(e.target.checked)}
                      />
                      <span className="text-sm">
                        Kopiera signeringsutkast
                        <span className="text-stone-500">
                          {' · '}
                          {sourcePlan!.signers_auto
                            ? 'signeringsval'
                            : `${sourcePlan!.signers.length} planerade undertecknare och signeringsval`}
                        </span>
                      </span>
                    </label>
                    <p className="mt-1 px-2 text-[11px] text-stone-500">
                      Namn och roll för undertecknarna samt signeringssätt, ort och datum, frister och
                      påminnelser. Kontaktuppgifter och personnummer kopieras inte.
                    </p>
                  </fieldset>
                )}

                {valueGroups.length > 0 && (
                  <fieldset className="mb-3">
                    <legend className="mb-1 text-xs font-medium uppercase tracking-wider text-stone-500">
                      Fältvärden
                    </legend>
                    <ul className="flex flex-col gap-1">
                      {valueGroups.map((g) => {
                        const checked = groupChecked[g.id] ?? false;
                        const expanded = expandedGroups.has(g.id);
                        const manualSel = g.manual.filter((f) =>
                          manualChecked.has(f.key),
                        ).length;
                        const total = g.auto.length + g.manual.length;
                        const selected = checked
                          ? g.auto.length + manualSel
                          : 0;
                        return (
                          <li
                            key={g.id}
                            className="rounded border border-stone-200"
                          >
                            <div className="flex items-center justify-between px-2 py-1">
                              <label className="flex cursor-pointer items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleGroup(g.id)}
                                />
                                <span className="text-sm font-medium">
                                  {g.label}
                                </span>
                                <span className="text-[11px] text-stone-500">
                                  {checked
                                    ? `${selected} av ${total} fält`
                                    : `${total} fält`}
                                </span>
                              </label>
                              {g.manual.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => toggleExpand(g.id)}
                                  className="text-[11px] text-stone-500 hover:underline"
                                >
                                  {expanded ? 'Dölj' : 'Anpassa'}
                                </button>
                              )}
                            </div>
                            {expanded && g.manual.length > 0 && (
                              <ul className="border-t border-stone-100 bg-stone-50 px-2 py-1">
                                {g.manual.map((f) => (
                                  <li key={f.key}>
                                    <label
                                      className={`flex items-center gap-2 py-0.5 text-xs ${
                                        checked
                                          ? 'cursor-pointer'
                                          : 'opacity-50'
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        disabled={!checked}
                                        checked={manualChecked.has(f.key)}
                                        onChange={() => toggleManual(f.key)}
                                      />
                                      <span>{f.label}</span>
                                      <span className="text-[10px] uppercase tracking-wide text-amber-700">
                                        per avtal
                                      </span>
                                    </label>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                    <p className="mt-1 px-2 text-[11px] text-stone-500">
                      Förkryssade grupper kopierar avtalets struktur.
                      Per-avtal-fält som hyra och datum kopieras bara om du
                      väljer dem under "Anpassa". Autoifyllda fält
                      (motpartsuppgifter, fastighetsadress) kopieras via valen
                      ovan.
                    </p>
                  </fieldset>
                )}
              </div>
            )}

            <div className="mt-2 flex items-center justify-end gap-2 border-t border-stone-200 pt-3">
              <button
                type="button"
                onClick={onClose}
                disabled={copying}
                className="rounded px-3 py-1.5 text-xs font-medium text-stone-600 hover:bg-stone-100 disabled:opacity-50"
              >
                Avbryt
              </button>
              <button
                type="button"
                onClick={() => void submit()}
                disabled={
                  copying ||
                  sourceDetailQ.isLoading ||
                  sourcePartiesQ.isLoading ||
                  !sourceDetail
                }
                className="rounded bg-stone-900 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
              >
                {copying ? 'Kopierar…' : 'Kopiera valda'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
